import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { ArticleCard } from '../components/ArticleCard';
import { VideoCard } from '../components/VideoCard';
import { ShortPill } from '../components/ShortPill';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Filter, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ articles: [], videos: [], shorts: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    contentType: searchParams.get('type') || 'all',
    category: searchParams.get('category') || '',
    sort: searchParams.get('sort') || 'relevance'
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      setSearchQuery(query);
      performSearch(query);
    }
  }, [searchParams]);

  const performSearch = async (query) => {
    if (!query || query.trim().length < 2) {
      setResults({ articles: [], videos: [], shorts: [], total: 0 });
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        q: query,
        sort: filters.sort
      });
      
      if (filters.contentType !== 'all') {
        params.append('content_type', filters.contentType);
      }
      if (filters.category) {
        params.append('category', filters.category);
      }

      const response = await axios.get(`${API_URL}/api/search?${params.toString()}`);
      setResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const params = new URLSearchParams({ q: searchQuery });
      if (filters.contentType !== 'all') params.append('type', filters.contentType);
      if (filters.category) params.append('category', filters.category);
      if (filters.sort !== 'relevance') params.append('sort', filters.sort);
      setSearchParams(params);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setResults({ articles: [], videos: [], shorts: [], total: 0 });
    setSearchParams({});
  };

  return (
    <>
      <Helmet>
        <title>{searchQuery ? `Search: ${searchQuery}` : 'Search'} · B&B Magazine</title>
      </Helmet>

      <HeaderNav />

      <main className="min-h-screen bg-background">
        <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Search Header */}
          <div className="mb-8">
            <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">Search</h1>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search articles, videos, shorts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <Button type="submit" disabled={!searchQuery.trim()}>
                Search
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
            </form>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-[hsl(var(--surface-1))] rounded-lg border border-border">
                <div>
                  <label className="text-sm font-medium mb-2 block">Content Type</label>
                  <Select
                    value={filters.contentType}
                    onValueChange={(value) => setFilters({ ...filters, contentType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Content</SelectItem>
                      <SelectItem value="article">Articles</SelectItem>
                      <SelectItem value="video">Videos</SelectItem>
                      <SelectItem value="short">Shorts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Category</label>
                  <Select
                    value={filters.category}
                    onValueChange={(value) => setFilters({ ...filters, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Categories</SelectItem>
                      <SelectItem value="features">Features</SelectItem>
                      <SelectItem value="interviews">Interviews</SelectItem>
                      <SelectItem value="brands">Brands</SelectItem>
                      <SelectItem value="creators">Creators</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Sort By</label>
                  <Select
                    value={filters.sort}
                    onValueChange={(value) => setFilters({ ...filters, sort: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relevance">Relevance</SelectItem>
                      <SelectItem value="date">Newest First</SelectItem>
                      <SelectItem value="views">Most Viewed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Results Count */}
            {searchParams.get('q') && (
              <p className="text-muted-foreground mt-4">
                {loading ? 'Searching...' : `Found ${results.total} result${results.total !== 1 ? 's' : ''} for "${searchParams.get('q')}"`}
              </p>
            )}
          </div>

          {/* Search Results */}
          {searchParams.get('q') && !loading && (
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="mb-6">
                <TabsTrigger value="all">
                  All ({results.total})
                </TabsTrigger>
                <TabsTrigger value="articles">
                  Articles ({results.articles.length})
                </TabsTrigger>
                <TabsTrigger value="videos">
                  Videos ({results.videos.length})
                </TabsTrigger>
                <TabsTrigger value="shorts">
                  Shorts ({results.shorts.length})
                </TabsTrigger>
              </TabsList>

              {/* All Results */}
              <TabsContent value="all">
                {results.total === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-muted-foreground text-lg">No results found. Try different keywords.</p>
                  </div>
                ) : (
                  <div className="space-y-12">
                    {results.articles.length > 0 && (
                      <div>
                        <h3 className="font-display text-2xl font-bold mb-6">Articles</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {results.articles.map((article) => (
                            <ArticleCard key={article.id} article={article} />
                          ))}
                        </div>
                      </div>
                    )}

                    {results.videos.length > 0 && (
                      <div>
                        <h3 className="font-display text-2xl font-bold mb-6">Videos</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {results.videos.map((video) => (
                            <VideoCard key={video.id} video={video} />
                          ))}
                        </div>
                      </div>
                    )}

                    {results.shorts.length > 0 && (
                      <div>
                        <h3 className="font-display text-2xl font-bold mb-6">Shorts</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {results.shorts.map((short) => (
                            <ShortPill key={short.id} short={short} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Articles Only */}
              <TabsContent value="articles">
                {results.articles.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-muted-foreground text-lg">No articles found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results.articles.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Videos Only */}
              <TabsContent value="videos">
                {results.videos.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-muted-foreground text-lg">No videos found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {results.videos.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Shorts Only */}
              <TabsContent value="shorts">
                {results.shorts.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-muted-foreground text-lg">No shorts found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {results.shorts.map((short) => (
                      <ShortPill key={short.id} short={short} />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          {/* Empty State */}
          {!searchParams.get('q') && !loading && (
            <div className="text-center py-20">
              <Search className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="font-display text-2xl font-bold mb-2">Start Searching</h2>
              <p className="text-muted-foreground">
                Search across articles, videos, and shorts to find what you're looking for.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
